import { useState } from 'react';
import { List } from 'lucide-react';
import { cn } from '../../lib/utils';

// TODO: these counts are dummy data for now — replace with live API values
// once the carrier-engagement data source is wired in.
const CHEVRON_STEPS = [
  { key: 'pending',    label: 'Pending',    count: 57, color: 'text-amber-700' },
  { key: 'contesting', label: 'Contesting', count: 74, color: 'text-sky-700' },
  { key: 'agreed',     label: 'Agreed',     count: 81, color: 'text-indigo-700' },
  { key: 'closed',     label: 'Closed',     count: 48, color: 'text-gray-900' },
];
const ALL_CHEVRON_COUNT = 260;

// Chevron shapes / border colour mirror the carrier-portal WorkflowStatusBar.
const CLIP_FIRST = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%)';
const CLIP_MID   = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%, 10px 50%)';
const CLIP_LAST  = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 10px 50%)';
const BORDER_COLOR = 'hsl(220 13% 86%)';

function ChevronBar({ steps, allCount, activeKey, onChange }) {
  const allActive = activeKey === 'all';
  return (
    <div data-testid="workflow-chevrons" className="flex items-center h-[68px] font-sans text-xs">
      <button
        type="button"
        data-testid="chevron-all"
        className={cn(
          'flex flex-col items-center justify-start w-[70px] h-full rounded-l-2xl border border-r-0 pt-1.5 pb-0 font-normal transition-colors',
          allActive
            ? 'bg-filter-bg-active text-filter-text-active'
            : 'bg-white text-foreground hover:bg-muted/50'
        )}
        style={{ zIndex: allActive ? 10 : 1, position: 'relative', borderColor: BORDER_COLOR }}
        onClick={() => onChange('all')}
      >
        <div className="h-[32px] flex items-center justify-center gap-1">
          <List className="h-3.5 w-3.5" />
          <span>All</span>
        </div>
        <div className="h-[24px] flex items-center justify-center">
          <span className={cn(
            'min-w-[1.4rem] h-[1.1rem] px-1 rounded-md flex items-center justify-center text-[10px] tabular-nums',
            allActive ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
          )}>{allCount}</span>
        </div>
      </button>

      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const isFirst = i === 0;
        const isActive = activeKey === step.key;
        const clipPath = isFirst ? CLIP_FIRST : isLast ? CLIP_LAST : CLIP_MID;
        return (
          <div
            key={step.key}
            className="relative -ml-[10px] shrink-0"
            style={{ width: '110px', height: '68px', zIndex: isActive ? 10 : i + 2 }}
          >
            <div
              className={cn('absolute inset-0', isLast && 'rounded-r-2xl')}
              style={{ clipPath, backgroundColor: BORDER_COLOR }}
            />
            <button
              type="button"
              data-testid={`chevron-${step.key}`}
              data-step={step.key}
              className={cn(
                'relative flex flex-col items-center justify-start pt-1.5 pb-0 transition-colors text-xs font-medium',
                isLast && 'rounded-r-2xl',
                isActive ? 'bg-filter-bg-active text-filter-text-active' : 'bg-white hover:bg-muted/50'
              )}
              style={{
                clipPath,
                margin: '1px',
                width: 'calc(100% - 2px)',
                height: 'calc(100% - 2px)',
              }}
              onClick={() => onChange(isActive ? 'all' : step.key)}
            >
              <div className={cn(
                'h-[32px] w-full flex items-center justify-center font-medium text-center leading-tight',
                isFirst ? 'px-4' : 'pl-6 pr-4',
                !isActive && step.color
              )}>
                {step.label}
              </div>
              <div className="h-[24px] w-full flex items-center justify-center">
                <span className={cn(
                  'min-w-[1.4rem] h-[1.1rem] px-1 rounded-md flex items-center justify-center text-[10px] tabular-nums',
                  isActive ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
                )}>{step.count}</span>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

function CarrierEngagementHeader() {
  // Placeholder state: not wired to data.
  const [activeStep, setActiveStep] = useState('closed');

  return (
    <div
      data-testid="carrier-engagement-header"
      className="flex items-center gap-4"
    >
      <ChevronBar
        steps={CHEVRON_STEPS}
        allCount={ALL_CHEVRON_COUNT}
        activeKey={activeStep}
        onChange={setActiveStep}
      />

      <div className="flex flex-col gap-1 shrink-0">
        <button
          type="button"
          data-testid="action-to-smartlight"
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-filter-bg-active px-4 py-1 text-[13px] font-medium text-filter-text-active transition-colors hover:opacity-90 h-[30px]"
        >
          <span aria-hidden="true">?</span>
          to SmartLight
        </button>
        <button
          type="button"
          data-testid="action-new-comments"
          className="inline-flex items-center justify-center rounded-md border border-border bg-card px-4 py-1 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 h-[30px]"
        >
          New Comments
        </button>
      </div>
    </div>
  );
}

export { CarrierEngagementHeader };
